/**
 * Practice data-access layer — the read side maps the shared `myPracticeInfo` query (the office of
 * the physician the member is assigned to) to the practice domain shape. Read-only: there is no
 * write side, members don't edit practice records.
 */

import { gqlRequest } from '@mdvip/shared/lib/graphql-client';
import type { MyPracticeInfoQuery, PracticeMembershipTier } from '@/graphql/generated/graphql';
import { MyPracticeInfoDocument } from '@/graphql/generated/graphql';

export interface PracticeAddress {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
}

export interface PracticeDayHours {
  start: string | null;
  end: string | null;
}

export interface PracticeOfficeHours {
  monday: PracticeDayHours;
  tuesday: PracticeDayHours;
  wednesday: PracticeDayHours;
  thursday: PracticeDayHours;
  friday: PracticeDayHours;
  saturday: PracticeDayHours;
  sunday: PracticeDayHours;
}

export interface PracticePhysician {
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  credentials: string | null;
}

/** One phone number on the membership card. The backend excludes any pair missing either side. */
export interface PracticeCardContact {
  phone: string;
  label: string;
}

export interface PracticeInfo {
  id: string;
  name: string | null;
  officePhone: string | null;
  emergencyPhone: string | null;
  address: PracticeAddress | null;
  officeHours: PracticeOfficeHours | null;
  physician: PracticePhysician | null;
  imageUrl: string | null;
  /** The membership card's own display name — a different Salesforce field from `name`. */
  cardDisplayName: string | null;
  coBrandName: string | null;
  cardContacts: PracticeCardContact[];
  additionalLine1: string | null;
  additionalLine2: string | null;
  membershipTier: PracticeMembershipTier | null;
}

type RawPractice = NonNullable<MyPracticeInfoQuery['myPracticeInfo']>;

/**
 * Shared across whichever member-scoped features read the physician/office record — same key as
 * both `PhysicianCard` and the office card (a later item) so mounting them together on the Profile
 * page fires the query once.
 */
export const practiceInfoKey = ['practice', 'myPracticeInfo'] as const;

/**
 * Salesforce text fields commonly come back as `""` rather than `null` for a genuinely blank value.
 * The backend already normalizes this (salesforce-account.mapper.ts), but re-normalizing here keeps
 * this boundary self-defending and matches the sibling `profileApi.ts` convention.
 */
function emptyToNull(value: string | null | undefined): string | null {
  return value ? value : null;
}

function mapAddress(address: RawPractice['address']): PracticeAddress | null {
  if (!address) {
    return null;
  }
  return {
    line1: emptyToNull(address.line1),
    line2: emptyToNull(address.line2),
    city: emptyToNull(address.city),
    state: emptyToNull(address.state),
    postalCode: emptyToNull(address.postalCode),
  };
}

function mapDayHours(hours: NonNullable<RawPractice['officeHours']>['monday']): PracticeDayHours {
  return { start: emptyToNull(hours.start), end: emptyToNull(hours.end) };
}

function mapOfficeHours(officeHours: RawPractice['officeHours']): PracticeOfficeHours | null {
  if (!officeHours) {
    return null;
  }
  return {
    monday: mapDayHours(officeHours.monday),
    tuesday: mapDayHours(officeHours.tuesday),
    wednesday: mapDayHours(officeHours.wednesday),
    thursday: mapDayHours(officeHours.thursday),
    friday: mapDayHours(officeHours.friday),
    saturday: mapDayHours(officeHours.saturday),
    sunday: mapDayHours(officeHours.sunday),
  };
}

function mapPhysician(physician: RawPractice['physician']): PracticePhysician | null {
  if (!physician) {
    return null;
  }
  return {
    firstName: emptyToNull(physician.firstName),
    middleName: emptyToNull(physician.middleName),
    lastName: emptyToNull(physician.lastName),
    credentials: emptyToNull(physician.credentials),
  };
}

export function mapToPracticeInfo(practice: RawPractice): PracticeInfo {
  return {
    id: practice.id,
    name: emptyToNull(practice.name),
    officePhone: emptyToNull(practice.officePhone),
    emergencyPhone: emptyToNull(practice.emergencyPhone),
    address: mapAddress(practice.address),
    officeHours: mapOfficeHours(practice.officeHours),
    physician: mapPhysician(practice.physician),
    imageUrl: emptyToNull(practice.imageUrl),
    cardDisplayName: emptyToNull(practice.cardDisplayName),
    coBrandName: emptyToNull(practice.coBrandName),
    cardContacts: practice.cardContacts.map(({ phone, label }) => ({ phone, label })),
    additionalLine1: emptyToNull(practice.additionalLine1),
    additionalLine2: emptyToNull(practice.additionalLine2),
    membershipTier: practice.membershipTier ?? null,
  };
}

export async function requestMyPracticeInfo(): Promise<RawPractice> {
  const data = await gqlRequest(MyPracticeInfoDocument);
  if (!data.myPracticeInfo) {
    // Unreachable in practice: the resolver always throws NotFoundException instead of resolving
    // with null (see the API's practice.service.ts) — the schema's `nullable: true` covers a case
    // this query never actually takes. Narrows the type without an unsafe cast.
    throw new Error('myPracticeInfo resolved without error but returned null');
  }
  return data.myPracticeInfo;
}
